import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { dict } from "@/lib/i18n";

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const [user, setUser] = useState(null);          // null | {} (logged in) | false (not)
    const [token, setToken] = useState(() => localStorage.getItem("cafe24_token"));
    const [lang, setLangState] = useState(() => localStorage.getItem("cafe24_lang") || "ar");
    const dir = lang === "ar" ? "rtl" : "ltr";

    const setLang = useCallback((l) => {
        setLangState(l);
        localStorage.setItem("cafe24_lang", l);
    }, []);

    const t = useCallback((key) => dict[lang]?.[key] ?? dict.en[key] ?? key, [lang]);

    useEffect(() => {
        document.documentElement.setAttribute("dir", dir);
        document.documentElement.setAttribute("lang", lang);
    }, [dir, lang]);

    useEffect(() => {
        if (!token) { setUser(false); return; }
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => { setUser(false); localStorage.removeItem("cafe24_token"); setToken(null); });
    }, [token]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("cafe24_token", data.token);
        setToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const pinLogin = async (pin) => {
        const { data } = await api.post("/auth/pin-login", { pin });
        localStorage.setItem("cafe24_token", data.token);
        setToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("cafe24_token");
        setToken(null);
        setUser(false);
    };

    return (
        <AppContext.Provider value={{ user, lang, dir, t, setLang, login, pinLogin, logout }}>
            {children}
        </AppContext.Provider>
    );
}

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be inside AppProvider");
    return ctx;
};
